import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Input } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../api/manufacturing";
import { ServiceOrderClient, type ServicePartAvailabilityDTO } from "../../api/servicedesk";
import { PartPicker } from "../manufacturing/PartPicker";

/** A part chosen to send: from a sold product's bill of materials, or found by search. */
export type PickedPart = {
  key: string;
  part_id: number;
  part_number: string;
  part_name: string;
  quantity: string;
  /** The sold product it belongs to; null for a part found by search. */
  sku: string | null;
};

/**
 * Service-shelf availability for a set of parts, keyed by part id. Shared by the
 * picker and the order's lines, so both say the same thing about the same part.
 */
export function useServiceAvailability(partIds: number[], excludeOrderId?: number) {
  const { http } = useAuth();
  const ids = useMemo(() => [...new Set(partIds)].sort((a, b) => a - b), [partIds]);
  const query = useQuery({
    queryKey: ["servicedesk", "availability", ids, excludeOrderId ?? null],
    queryFn: () => new ServiceOrderClient(http).availability(ids, excludeOrderId),
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
  return useMemo(() => new Map((query.data ?? []).map((row) => [row.part_id, row])), [query.data]);
}

/**
 * Can this go from the service shelf? Green when the shelf covers the quantity,
 * amber when it covers part of it, grey when the part is not there (with the
 * plant's stock beside it, which is a transfer rather than a production order).
 */
export function AvailabilityBadge({ availability, needed }: { availability: ServicePartAvailabilityDTO | undefined; needed?: number }) {
  const { t } = useTranslation();
  if (!availability) return null;
  const code = availability.warehouse_code ?? "SERV";
  const free = Number(availability.available);
  const want = needed != null && needed > 0 ? needed : 1;
  const title = t("sd.avail.title", "On hand {{onHand}}, promised to other open orders {{promised}}", {
    onHand: formatNumber(availability.on_hand),
    promised: formatNumber(availability.promised),
  });
  if (free >= want) {
    return (
      <span title={title}>
        <Badge variant="success" size="sm">
          {t("sd.avail.in-stock", "{{code}}: {{n}} in stock", { code, n: formatNumber(free) })}
        </Badge>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1" title={title}>
      {free > 0 ? (
        <Badge variant="warning" size="sm">
          {t("sd.avail.short", "{{code}}: {{n}} of {{want}}", { code, n: formatNumber(free), want: formatNumber(want) })}
        </Badge>
      ) : (
        <Badge variant="neutral" size="sm">
          {t("sd.avail.none", "Not at {{code}}", { code })}
        </Badge>
      )}
      {Number(availability.plant_on_hand) > 0 && (
        <Badge variant="info" size="sm">
          {t("sd.avail.plant", "Plant: {{n}}", { n: formatNumber(availability.plant_on_hand) })}
        </Badge>
      )}
    </span>
  );
}

/**
 * The parts to send, picked from what was sold or found by search.
 *
 * <p>Every product on the sale (and on the sale behind a replacement order) is
 * listed with its bill of materials, so the agent ticks the panel or the
 * hardware the customer is missing and sets how many. By default only the kinds
 * of part the service desk sends are shown; packaging and raw material are a
 * switch away. Each part says whether the service shelf can send it.</p>
 *
 * <p>Controlled: the caller owns what is picked, so the same picker serves the
 * new-order dialog (before the order exists) and Add part on an order.</p>
 */
export function ServicePartsPicker({
  saleNumber,
  excludeOrderId,
  picked,
  onChange,
}: {
  /** The Nexera sale to list parts from; null lists nothing and leaves search. */
  saleNumber: number | null;
  /** The order being edited, whose own lines must not hide the shelf stock. */
  excludeOrderId?: number;
  picked: Record<string, PickedPart>;
  onChange: (picked: Record<string, PickedPart>) => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new ServiceOrderClient(http), [http]);
  const [showAll, setShowAll] = useState(false);

  const sale = useQuery({
    queryKey: ["servicedesk", "sale-parts", saleNumber],
    queryFn: () => orders.salePartsOfSale(saleNumber!),
    enabled: saleNumber != null,
    staleTime: 60_000,
  });
  const products = useMemo(() => sale.data ?? [], [sale.data]);
  const chosen = Object.values(picked);
  const custom = chosen.filter((part) => part.sku == null);

  const partIds = useMemo(
    () => [
      ...products.flatMap((product) => product.components.filter((component) => showAll || component.usual).map((component) => component.part_id)),
      ...custom.map((part) => part.part_id),
    ],
    [products, custom, showAll],
  );
  const availability = useServiceAvailability(partIds, excludeOrderId);

  const toggle = (part: Omit<PickedPart, "quantity">, quantity: number) => {
    const next = { ...picked };
    if (next[part.key]) {
      delete next[part.key];
    } else {
      next[part.key] = { ...part, quantity: String(Number(quantity) > 0 ? Number(quantity) : 1) };
    }
    onChange(next);
  };
  const setQuantity = (key: string, quantity: string) => {
    const current = picked[key];
    if (current) onChange({ ...picked, [key]: { ...current, quantity } });
  };

  const quantityInput = (part: PickedPart) => (
    <Input
      aria-label={t("column.bom.quantity", "Quantity")}
      type="number"
      min={0}
      step="any"
      className="h-7 w-20 py-0 text-[13px]"
      invalid={!(Number(part.quantity) > 0)}
      value={part.quantity}
      onChange={(event) => setQuantity(part.key, event.target.value)}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {saleNumber == null ? null : sale.isLoading ? (
        <p className="text-[13px] text-ink-muted">{t("common.loading", "Loading…")}</p>
      ) : products.length === 0 ? (
        <p className="text-[13px] text-ink-muted">{t("sd.line.sale-empty", "Nexera order #{{n}} has no products on it.", { n: saleNumber })}</p>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="text-[12px] font-medium uppercase tracking-wide text-ink-muted">{t("sd.line.from-sale", "From the sale")}</span>
            <label className="ml-auto flex cursor-pointer items-center gap-2 text-[12px] text-ink-muted">
              <input type="checkbox" checked={showAll} onChange={(event) => setShowAll(event.target.checked)} className="size-3.5 accent-[var(--accent)]" />
              {t("sd.line.show-all", "Show packaging and raw material")}
            </label>
          </div>
          <div className="max-h-[45vh] overflow-auto rounded-md border border-line">
            {products.map((product) => {
              const rows = product.components.filter((component) => showAll || component.usual);
              return (
                <section key={`${String(product.order_number)}|${product.sku}`} className="border-b border-line last:border-b-0">
                  <header className="sticky top-0 z-10 flex flex-wrap items-baseline gap-2 bg-surface-sunk px-3 py-1.5">
                    <span className="text-[13px] font-semibold">{product.sku}</span>
                    <span className="text-[12px] text-ink-muted">{product.description}</span>
                    <span className="ml-auto text-[11px] text-ink-faint">
                      {t("sd.line.on-order", "Nexera #{{n}}", { n: product.order_number })}
                      {product.part_number ? ` · ${product.part_number}` : ""}
                    </span>
                  </header>
                  {product.part_id == null ? (
                    <p className="px-3 py-2 text-[12px] text-ink-muted">{t("sd.line.no-part", "No part for this SKU, so no bill of materials.")}</p>
                  ) : rows.length === 0 ? (
                    <p className="px-3 py-2 text-[12px] text-ink-muted">{t("sd.line.no-bom", "No bill of materials for this part.")}</p>
                  ) : (
                    <ul>
                      {rows.map((component) => {
                        // The same SKU can be on the sale and on the replacement order behind it.
                        const key = `${String(product.order_number)}|${product.sku}|${component.path}`;
                        const chosenPart = picked[key];
                        return (
                          <li key={key} className="flex min-h-9 items-center gap-2 px-3 py-1 hover:bg-surface-sunk">
                            <label
                              className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-[13px]"
                              style={{ paddingLeft: `${String((component.level - 1) * 16)}px` }}
                            >
                              <input
                                type="checkbox"
                                checked={chosenPart != null}
                                onChange={() =>
                                  toggle(
                                    { key, part_id: component.part_id, part_number: component.part_number, part_name: component.part_name, sku: product.sku },
                                    component.quantity,
                                  )
                                }
                                className="size-3.5 shrink-0 accent-[var(--accent)]"
                              />
                              <span className="shrink-0 font-medium">{component.part_number}</span>
                              <span className="truncate text-ink-muted">{component.part_name}</span>
                            </label>
                            <AvailabilityBadge
                              availability={availability.get(component.part_id)}
                              needed={chosenPart ? Number(chosenPart.quantity) : undefined}
                            />
                            <span className="shrink-0 text-[11px] tabular-nums text-ink-faint">
                              × {formatNumber(component.quantity)} {component.unit_code ?? ""}
                            </span>
                            {chosenPart && quantityInput(chosenPart)}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-medium uppercase tracking-wide text-ink-muted">
          {saleNumber == null ? t("sd.line.find-part", "Parts") : t("sd.line.other-part", "Another part")}
        </span>
        <div className="max-w-md">
          <PartPicker
            exclude={custom.map((part) => part.part_id)}
            onPick={(part) =>
              toggle({ key: `custom|${String(part.id)}`, part_id: part.id, part_number: part.number, part_name: part.name, sku: null }, 1)
            }
            placeholder={t("part.picker.placeholder", "Search a part by number or name")}
          />
        </div>
        {custom.length > 0 && (
          <ul className="rounded-md border border-line">
            {custom.map((part) => (
              <li key={part.key} className="flex items-center gap-2 border-b border-line px-3 py-1 text-[13px] last:border-b-0">
                <span className="font-medium">{part.part_number}</span>
                <span className="min-w-0 flex-1 truncate text-ink-muted">{part.part_name}</span>
                <AvailabilityBadge availability={availability.get(part.part_id)} needed={Number(part.quantity)} />
                {quantityInput(part)}
                <Button variant="ghost" size="sm" onClick={() => toggle(part, 1)}>
                  {t("button.remove", "Remove")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
